import { motion } from "framer-motion"
import { FormEvent, useCallback, useState } from "react"
import { signOut as signOutFromFirebase } from "firebase/auth"
import Link from "next/link"
import { useRouter } from "next/router"
import { posthog } from "posthog-js"
import Head from "next/head"
import { Button } from "../components/Button"
import { Footer } from "../components/Footer"
import { Header } from "../components/Header"
import { sendSignInLink } from "../utils/sendSignInLink"
import { useAuth } from "../utils/useAuth"
import { sendSupportMail } from "../utils/useMail"
import { auth } from "../utils/init"
import { PageProps } from "../utils/types"

const headerVariants = {
  animate: {
    opacity: 1,
  },
  initial: {
    opacity: 0,
  },
}

const descVariants = {
  animate: {
    opacity: 1,
    transition: {
      delay: 0.05,
    },
  },
  initial: {
    opacity: 0,
  },
}

const emailVariants = {
  animate: {
    opacity: 1,
    transition: {
      delay: 0.1,
    },
  },
  initial: {
    opacity: 0,
  },
}

export default function AccountPage(props: PageProps) {
  const [linkSentTo, setLinkSentTo] = useState("")
  const [activated, setActivated] = useState(false)
  const [activationError, setActivationError] = useState("")
  const [linkCode, setLinkCode] = useState("")
  const router = useRouter()
  const isSuccess = router.query.buy === "success"
  const { user } = useAuth()
  const sendLinkCode = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      if (!auth || !auth.currentUser) {
        return
      }
      const idToken = await auth.currentUser.getIdToken()
      await fetch("/api/activate", {
        body: JSON.stringify({
          idToken,
          linkCode,
          postHogId: posthog.get_distinct_id(),
        }),
        headers: {
          "Content-Type": "application/json",
        },
        method: "POST",
      })
        .then((res) => {
          if (res.status === 200) {
            setActivated(true)
          } else {
            setActivationError(
              (res as unknown as { error: string }).error ||
                "Something went wrong."
            )
          }
        })
        .catch((e: Error) => {
          setActivationError(e.message)
        })
    },
    [linkCode]
  )
  const updateSubscription = useCallback(() => {
    if (!props.frontenderSettings?.updateUrl) {
      // eslint-disable-next-line no-alert
      window.alert(
        "No update URL was found for your account. Please contact us so we can handle it for you manually."
      )
      return
    }
    ;(window as any).Paddle.Checkout.open({
      override: props.frontenderSettings?.updateUrl,
    })
  }, [props.frontenderSettings?.updateUrl])
  const cancelSubscription = useCallback(() => {
    if (!props.frontenderSettings?.cancelUrl) {
      // eslint-disable-next-line no-alert
      window.alert(
        "No cancel URL was found for your account. Please contact us so we can handle it for you manually."
      )
      return
    }
    ;(window as any).Paddle.Checkout.open({
      override: props.frontenderSettings?.cancelUrl,
    })
  }, [props.frontenderSettings?.cancelUrl])
  const handleSubmit = useCallback((e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const email = (e.currentTarget[0] as HTMLInputElement)?.value as string
    sendSignInLink(email)
      .then(() => {
        setLinkSentTo(email)
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error(err)
        // eslint-disable-next-line no-alert
        window.alert(
          "Sorry, something went wrong sending you the verification email. Please try again or contact us."
        )
      })
  }, [])
  const signOut = useCallback(() => {
    signOutFromFirebase(auth)
    posthog.reset()
  }, [])
  const isLifetime = props.frontenderSettings?.plan?.includes("lifetime")
  const shouldActivate =
    props.frontenderSettings &&
    props.frontenderSettings.plan &&
    !props.frontenderSettings.figmaUserId
  return (
    <>
      <Head>
        <title>Frontender | Speed up your front-end workflow</title>
        <meta name="theme-color" content="#18181b" />
        <meta property="og:image" content="https://www.frontender.io/og.png" />
        <meta property="og:image:type" content="image/png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="627" />
        <meta property="og:title" content="Frontender" />
        <meta
          property="description"
          content="Speed up your front-end workflow."
        />
        <meta
          property="og:description"
          content="Speed up your front-end workflow."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.frontender.io/" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link
          rel="apple-touch-icon"
          sizes="180x180"
          href="/apple-touch-icon.png"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="32x32"
          href="/favicon-32x32.png"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="16x16"
          href="/favicon-16x16.png"
        />
        <link rel="manifest" href="/site.webmanifest" />
        <meta name="msapplication-TileColor" content="#da532c" />
      </Head>
      <motion.div
        initial="initial"
        animate="animate"
        className="flex min-h-screen select-none bg-gradient-to-b from-zinc-900 via-zinc-900 to-zinc-800 p-4"
      >
        <div className="flex flex-1 flex-col overflow-hidden border-zinc-700 p-4 pt-1 sm:border sm:p-7 sm:pt-3">
          <Header />
          <div className="max-w-screen-2xl py-10 sm:px-4 lg:px-12 lg:py-16 xl:py-20">
            <motion.div
              variants={headerVariants}
              className="inline-block bg-gradient-to-r from-sky-300 to-fuchsia-300 bg-clip-text pb-1 text-3xl font-semibold leading-tight text-transparent sm:text-5xl sm:tracking-tight"
            >
              {user?.email ? (
                <span className="break-all">{user.email}</span>
              ) : isSuccess ? (
                "Payment successful"
              ) : (
                "Manage your account"
              )}
            </motion.div>
            {isSuccess ? (
              <>
                <motion.p
                  variants={descVariants}
                  className="mt-9 text-xl font-semibold leading-relaxed text-zinc-200"
                >
                  One more thing to do...
                </motion.p>
                <motion.p
                  variants={descVariants}
                  className="mb-10 max-w-xl text-xl leading-relaxed text-zinc-400"
                >
                  To use Frontender Professional, you need to link it to your
                  plugin. This takes just a minute. Please log in below using
                  the email address you used to purchase Frontender.
                </motion.p>
              </>
            ) : (
              <motion.p
                variants={descVariants}
                className="mb-10 mt-3 text-xl leading-relaxed text-zinc-300"
              >
                {user ? (
                  <>
                    Manage your {isLifetime ? "account" : "subscription"} below,
                    or{" "}
                    <button
                      type="button"
                      onClick={sendSupportMail}
                      className="text-zinc-200 underline decoration-zinc-500 decoration-2 underline-offset-4"
                    >
                      contact us
                    </button>{" "}
                    if you need help.
                  </>
                ) : (
                  "Please sign in to access your account and billing details."
                )}
              </motion.p>
            )}
            {user ? (
              <motion.div
                variants={emailVariants}
                key={linkSentTo}
                className="font-semibold text-zinc-200"
              >
                {shouldActivate && (
                  <div className="mb-10 inline-block w-full max-w-lg rounded-lg border border-sky-300 px-8 py-6 text-zinc-400">
                    {activated ? (
                      <>
                        <div className="mb-3 text-xl font-semibold text-white">
                          Your plugin is activated!
                        </div>
                        <p className="font-normal leading-relaxed">
                          <span className="font-medium text-sky-300">
                            Make sure to close the plugin
                          </span>{" "}
                          and open it again. This will give you access to
                          unlimited conversions and all other professional
                          features. Please{" "}
                          <button
                            type="button"
                            onClick={sendSupportMail}
                            className="underline decoration-zinc-600 decoration-1 underline-offset-2"
                          >
                            contact us
                          </button>{" "}
                          if you have any questions. Happy coding! ✨
                        </p>
                      </>
                    ) : (
                      <>
                        <div className="mb-6 text-xl font-semibold text-white">
                          Activate your plugin
                        </div>
                        <div className="flex">
                          <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-sky-300 font-bold text-sky-900">
                            1
                          </div>
                          <div className="px-4 py-1">
                            <div className="font-medium text-white">
                              Get the plugin
                            </div>
                            <p className="font-normal">
                              If you already used the plugin, you can skip this
                              step. Otherwise,{" "}
                              <Link href="/install" target="_blank">
                                go here
                              </Link>{" "}
                              to get the plugin.
                            </p>
                          </div>
                        </div>
                        <div className="mt-4 flex">
                          <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-purple-300 font-bold text-purple-900">
                            2
                          </div>
                          <div className="px-4 py-1">
                            <div className="font-medium text-white">
                              Copy your link code
                            </div>
                            <p className="font-normal">
                              Open the plugin and click &quot;Copy link
                              code&quot;. If you ran out of conversions for this
                              month, you can find the button on the bottom of
                              the plugin. If not, go to Settings, then click
                              Plan, then click the &quot;Copy link code&quot;
                              button.
                            </p>
                            <div className="mt-7 inline-block overflow-hidden rounded-lg border border-zinc-800">
                              {/* eslint-disable-next-line @next/next/no-img-element */}
                              <img
                                src="/code.png"
                                alt="Location of code link button"
                                width={300}
                                height={200}
                              />
                            </div>
                          </div>
                        </div>
                        <div className="mt-4 flex">
                          <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-emerald-300 font-bold text-emerald-900">
                            3
                          </div>
                          <div className="px-4 py-1">
                            <div className="font-medium text-white">
                              Paste your link code here to activate
                            </div>
                            <form
                              onSubmit={sendLinkCode}
                              className="mt-4 flex w-full space-x-4 font-normal"
                            >
                              <input
                                placeholder="Your link code"
                                type="text"
                                value={linkCode}
                                // eslint-disable-next-line react/jsx-no-bind
                                onChange={(e) => setLinkCode(e.target.value)}
                                required
                                className="w-full flex-1 rounded-lg border border-zinc-700 bg-transparent px-4 py-2 text-white focus:border-sky-500 focus:ring-0 focus:ring-offset-0"
                              />
                              <div className="self-center">
                                <Button type="submit">Activate</Button>
                              </div>
                            </form>
                            {activationError && (
                              <div className="mt-4 font-normal text-red-400">
                                {activationError} Please try again or{" "}
                                <button
                                  type="button"
                                  onClick={sendSupportMail}
                                  className="underline decoration-red-400/50 underline-offset-4"
                                >
                                  contact us
                                </button>
                                .
                              </div>
                            )}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )}
                {!shouldActivate && (
                  <div>
                    <button disabled type="button" className="p-2 opacity-50">
                      &rarr; Activate Figma plugin (done!)
                    </button>
                  </div>
                )}
                {!isLifetime && (
                  <>
                    <div>
                      <button
                        onClick={updateSubscription}
                        type="button"
                        className="p-2 hover:text-white"
                      >
                        &rarr; Update payment method
                      </button>
                    </div>
                    <div>
                      <button
                        onClick={cancelSubscription}
                        type="button"
                        className="p-2 hover:text-white"
                      >
                        &rarr; Cancel subscription
                      </button>
                    </div>
                  </>
                )}
                <div>
                  <button
                    type="button"
                    onClick={sendSupportMail}
                    className="p-2 hover:text-white"
                  >
                    &rarr; Contact support
                  </button>
                </div>
                <div>
                  <button
                    onClick={signOut}
                    type="button"
                    className="p-2 hover:text-white"
                  >
                    &rarr; Sign out
                  </button>
                </div>
              </motion.div>
            ) : (
              <motion.div variants={emailVariants} key={linkSentTo}>
                {linkSentTo ? (
                  <div className="inline-block rounded-lg border border-zinc-700 px-6 py-4 text-zinc-400">
                    <div className="font-semibold text-zinc-200">
                      Please check your inbox
                    </div>
                    <p>
                      We sent a link to{" "}
                      <span className="mx-0.5 rounded bg-zinc-800 px-1">
                        {linkSentTo}
                      </span>
                      &ndash; please click it to log in.
                    </p>
                  </div>
                ) : (
                  <form
                    onSubmit={handleSubmit}
                    className="flex w-full max-w-sm space-x-4"
                  >
                    <input
                      placeholder="Your email address"
                      type="email"
                      required
                      className="flex-1 rounded-lg border border-zinc-700 bg-transparent px-4 py-2 text-white focus:border-sky-500 focus:ring-0 focus:ring-offset-0"
                    />
                    <div className="self-center">
                      <Button type="submit">Continue</Button>
                    </div>
                  </form>
                )}
              </motion.div>
            )}
          </div>
          <Footer
            acceptedCookies={props.acceptedCookies}
            setAcceptedCookies={props.setAcceptedCookies}
          />
        </div>
        <div className="animate-blob pointer-events-none fixed -right-[70vw] -top-[70vh] h-[120vh] w-[120vw] rounded-full bg-rose-700 opacity-10 blur-3xl" />
      </motion.div>
    </>
  )
}

AccountPage.displayName = "AccountPage"
